import React, { useEffect, useRef } from 'react';
import type { BrandConfig } from '@lds-efsp/brand-config';
import type { IconsType } from '../../../types/admin';

interface SetupDetailDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  subtitle?: string;
  brand: BrandConfig;
  Icons: IconsType;
  children: React.ReactNode;
  footer?: React.ReactNode;
  width?: 'sm' | 'md' | 'lg' | 'xl';
}

const SetupDetailDrawer: React.FC<SetupDetailDrawerProps> = ({
  isOpen,
  onClose,
  title,
  subtitle,
  brand,
  Icons,
  children,
  footer,
  width = 'md',
}) => {
  const drawerRef = useRef<HTMLDivElement>(null);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  // Lock body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]);

  const widthClasses = {
    sm: 'max-w-sm',
    md: 'max-w-xl',
    lg: 'max-w-2xl',
    xl: 'max-w-4xl',
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-40 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Drawer */}
      <div
        ref={drawerRef}
        className={`fixed inset-y-0 right-0 ${widthClasses[width]} w-full bg-white shadow-xl z-50 flex flex-col transform transition-transform duration-300 ease-in-out`}
        style={{
          transform: 'translateX(0)',
        }}
        role="dialog"
        aria-modal="true"
        aria-labelledby="drawer-title"
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b shrink-0"
          style={{ borderColor: brand.colors.border }}
        >
          <div>
            <h2
              id="drawer-title"
              className="text-lg font-semibold"
              style={{ color: brand.colors.secondary }}
            >
              {title}
            </h2>
            {subtitle && (
              <p
                className="text-sm mt-0.5"
                style={{ color: brand.colors.textLight }}
              >
                {subtitle}
              </p>
            )}
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            aria-label="Close drawer"
          >
            <Icons.X className="w-5 h-5" style={{ color: brand.colors.textLight }} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {children}
        </div>

        {/* Footer */}
        {footer && (
          <div
            className="shrink-0 px-6 py-4 border-t"
            style={{ borderColor: brand.colors.border }}
          >
            {footer}
          </div>
        )}
      </div>
    </>
  );
};

export default SetupDetailDrawer;
